<script lang="ts" setup>
import SuspendedErrorHandler from '@/components/suspense/SuspendedErrorHandler.vue';
import FolderList from '@/components/sidebar/FolderList.vue';
import SiteLogo from '@/components/SiteLogo.vue';
import { defineAsyncComponent, inject, ref, watch } from 'vue';
import { ROUTE_NAMES } from '@/router/conf';
import { useRoute, useRouter } from 'vue-router';
import { REFRESH_SIDEBAR_MENU_ON_MOBILE } from '@/types/components/settings/folders/Consts';
import type { ISidebarRefreshCallback } from '@/types/components/settings/folders/Settings';
import { EDisabledFeatures } from '@/types/composables/useDisabledFeatureEnums';
import useDisabledFeature from '@/composables/useDisabledFeature';
import useAuthenticatedQuery from '@/composables/apiServices/useAuthenticatedQuery';
import type { IMailboxSidebarProps } from '@/types/components/sidebar/MailboxSidebar';
import useUserService from '@/composables/apiServices/useUserService';
import FoldersUnreadToggle from './FoldersUnreadToggle.vue';

const ContactsList = defineAsyncComponent(() => import('@/components/sidebar/ContactsList.vue'));

const props = defineProps<IMailboxSidebarProps>();
const router = useRouter();
const route = useRoute();
const { userIndex } = useAuthenticatedQuery();
const { isFeatureDisabled } = useDisabledFeature();
const { attributes } = useUserService();
const { data: userAttributes } = attributes();

const isContactsDisabled = ref(true);

const injectSidebarRefresh = inject<ISidebarRefreshCallback>(REFRESH_SIDEBAR_MENU_ON_MOBILE);

const handleViewAllSettings = async () => {
  await router.push({
    name: ROUTE_NAMES.SETTINGS_LAYOUT,
    params: {
      userIndex: route.params.userIndex
    }
  });

  if (!injectSidebarRefresh) {
    throw new Error('Handler is not provided');
  }
  injectSidebarRefresh(true);
};
watch(
  [userIndex, userAttributes],
  async ([, newUserAttributes]) => {
    isContactsDisabled.value =
      (await isFeatureDisabled(EDisabledFeatures.CONTACTS)) ||
      !newUserAttributes?.contactAccordionShownState;
  },
  { immediate: true }
);
</script>

<template>
  <VList v-if="props.isSmallDisplay" :lines="false" class="pl-6 pr-3 pt-3 pb-0">
    <VListItem class="pa-0">
      <template #prepend>
        <SiteLogo class="d-block" logo-color="on-alert" />
      </template>

      <template #append>
        <FoldersUnreadToggle />
        <TextBtn
          @click="handleViewAllSettings"
          data-test-id="sidebar-redirect-settings"
          sentry-unmask
          size="40"
        >
          <VIcon color="alert-close" size="large">mdi-cog-outline</VIcon>
        </TextBtn>
      </template>
    </VListItem>
  </VList>

  <div class="sidebar__content">
    <SuspendedErrorHandler>
      <FolderList />
    </SuspendedErrorHandler>

    <SuspendedErrorHandler v-if="!isContactsDisabled">
      <ContactsList />
    </SuspendedErrorHandler>
  </div>
</template>
<style lang="scss" scoped>
@use '../../assets/styles/settings' as settings;
@use 'sass:map';

.sidebar__content {
  overflow-y: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
  height: calc(100% - 4.25rem);

  @media screen and #{map.get(settings.$display-breakpoints, 'md-and-up')} {
    height: calc(100% - 2.55rem);
  }

  :deep(.folder-list) {
    flex-shrink: 1;
  }

  :deep(.contacts-list) {
    margin-top: auto;
  }
}
</style>
